import { CHAT_CONSTANTS } from '@/shared/constants/chatConstants';
import React, { useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Username } from '@/features/users';
import { isInternalMeetLink, handleJoinMeetLink } from '@/features/calendar/utils/meetLinkHelper';
import { blockOfflineAction } from '@/shared/utils';

/* -------------------------------------------------- */
/* Build mention regex ONCE                           */
/* -------------------------------------------------- */
const escapedMention = CHAT_CONSTANTS.MENTION_FORMAT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const mentionRegex = new RegExp(escapedMention, 'g');
const splitRegex = new RegExp(`(${escapedMention}|https?:\\/\\/[^\\s]+|www\\.[^\\s]+)`, 'g');
const urlRegex = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/;

/**
 * Pure utility for plain-text formatting (e.g., notifications, recent chat labels)
 */
export const formatPlainTextMention = (content = '', mentionedUsersIds = [], users = {}) => {
   if (!content || typeof content !== 'string') return content;

   let index = 0;
   return content
      .replace(mentionRegex, () => {
         const userId = mentionedUsersIds[index];
         const user = users[userId] || null;
         index++;

         const displayName = user?.nickName || userId || '';
         return displayName ? `${CHAT_CONSTANTS.CHIP_PREFIX}${displayName}` : '';
      })
      .replace(/\u00A0/g, ' ');
};

export const useMessageFormatter = (
   content = '',
   { mentionedUsersIds = [], isClickable = true, plainText = false } = {},
) => {
   const dispatch = useDispatch();
   /* Subscribe only to mentioned users */
   const mentionedUsersProfileMap = useSelector(state => {
      const allUsers = state.userInfoData?.users || {};
      return allUsers; // We get the map here to pass it to the utility
   }, shallowEqual);

   const formattedMessage = useMemo(() => {
      if (!content || typeof content !== 'string') return content;

      /* ---------------- Plain Text ---------------- */
      if (plainText) {
         return formatPlainTextMention(content, mentionedUsersIds, mentionedUsersProfileMap);
      }

      /* ---------------- React Nodes ---------------- */
      let mentionIndex = 0;

      const parts = content.split(splitRegex);

      return parts.map((part, i) => {
         /* Mention */
         if (part === CHAT_CONSTANTS.MENTION_FORMAT) {
            const userId = mentionedUsersIds[mentionIndex];
            mentionIndex++;

            if (isClickable) {
               return (
                  <Username
                     key={`mention-${i}-${userId}`}
                     userId={userId}
                     prefix={CHAT_CONSTANTS.CHIP_PREFIX}
                     className={CHAT_CONSTANTS.MENTION_CHIP_CLASS}
                     contentEditable="false"
                     suppressContentEditableWarning={true}
                     data-user-id={userId}
                  />
               );
            }

            const user = mentionedUsersProfileMap[userId];
            const name = user?.nickName || userId || '';
            return name ? `${CHAT_CONSTANTS.CHIP_PREFIX}${name}` : '';
         }

         /* URL */
         if (urlRegex.test(part)) {
            const href = part.startsWith('www.') ? `https://${part}` : part;

            const handleLinkClick = e => {
               if (isInternalMeetLink(href)) {
                  e.preventDefault();
                  if (blockOfflineAction()) return;
                  handleJoinMeetLink(href, dispatch);
               }
            };

            return isClickable ? (
               <a
                  key={`link-${i * 2 + 1}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="message-link"
                  onClick={handleLinkClick}
               >
                  {part}
               </a>
            ) : (
               part
            );
         }

         return part;
      });
   }, [content, mentionedUsersProfileMap, mentionedUsersIds, plainText, isClickable, dispatch]);

   return formattedMessage;
};
